import { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Loader } from '@givebutter/primitives/loader';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAccountAddress } from 'api/account';
import { createEngageMessageStripeSession } from 'api/engage';
import * as LetterConfirmMailingDialog from 'assets/js/pages/engage/letters/LetterConfirmMailingDialog';
import { LetterDownloadPreviewButton } from 'assets/js/pages/engage/letters/LetterDownloadPreviewButton';
import { LetterExcludedRecipientCounts } from 'assets/js/pages/engage/letters/LetterExcludedRecipientCounts';
import * as LetterMailProofDialog from 'assets/js/pages/engage/letters/LetterMailProofDialog';
import { LetterPreview } from 'assets/js/pages/engage/letters/LetterPreview';
import { RecipientCountButton } from 'assets/js/pages/engage/letters/RecipientCountButton';
import { POSTAGE_TYPES } from 'assets/js/pages/engage/letters/constants';
import { LetterDraftFormValues } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useCurrentLetterPreview } from 'assets/js/pages/engage/letters/useCurrentLetterPreview';
import { useLetterMeta } from 'assets/js/pages/engage/letters/useLetterMeta';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';
import { GivebutterAxiosError } from 'types/error';

const LetterConfirmationEasySend = () => {
    const { isBodyEmpty, noRecipientCount } = useLetterMeta();
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { data: letter, isFetching: isFetchingLetter } = useCurrentLetter();
    const { control, path, watch } = Form.useGivebutterFormContext<LetterDraftFormValues>();
    const [processingSpeed, returnAddress] = watch(['processingSpeed', 'returnAddress']);
    const query = useCurrentLetterPreview();
    const { data: previewStatus, isFetching: isFetchingPreviewStatus } = query;
    const hasReturnAddressWithName = useHasAddressWithName(returnAddress);
    const hasValidLetter = !isBodyEmpty && !noRecipientCount && hasReturnAddressWithName;
    const { data: paymentData, isLoading: isLoadingPaymentData } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageStripeSession(accountId, letter.id),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => createEngageMessageStripeSession(accountId, letter.id),
                hasValidLetter,
            ),
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });
    const estimatedShippingDates = useMemo(() => {
        const bestCaseDate = DateTime.fromISO(letter.mailings_estimated_delivery_dates.best_case);
        const worstCaseDate = DateTime.fromISO(letter.mailings_estimated_delivery_dates.worst_case);

        const bestCaseFormat = bestCaseDate.toFormat('LLL d');
        const worstCaseFormat = worstCaseDate.toFormat('LLL d');

        if (bestCaseDate.month === worstCaseDate.month) {
            return `${bestCaseFormat} - ${worstCaseDate.day}`;
        } else {
            return `${bestCaseFormat} - ${worstCaseFormat}`;
        }
    }, [
        letter.mailings_estimated_delivery_dates.best_case,
        letter.mailings_estimated_delivery_dates.worst_case,
    ]);

    return (
        <div className={'flex flex-col gap-4'}>
            {isBodyEmpty && (
                <AlertNotification.Root className={'w-full'} type={'error'}>
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            The content of your letter is empty
                        </AlertNotification.Title>
                        <AlertNotification.Description>
                            Navigate to the "Compose" step to finalize your letter's content.
                        </AlertNotification.Description>
                        <Button
                            variant="none"
                            color="error"
                            size="xs"
                            className="font-medium"
                            onClick={() =>
                                history.push(
                                    `/accounts/${accountId}/engage/letters/draft/${letter.id}/edit/compose`,
                                )
                            }
                        >
                            <span>Compose letter</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}
            {noRecipientCount && (
                <AlertNotification.Root className={'w-full'} type={'error'}>
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            No contacts have been selected
                        </AlertNotification.Title>
                        <AlertNotification.Description>
                            Navigate to the 'Recipients' step where you can choose the contacts for
                            whom you'd like to send letters to.
                        </AlertNotification.Description>
                        <Button
                            variant="none"
                            color="error"
                            size="xs"
                            className="font-medium"
                            onClick={() =>
                                history.push(
                                    `/accounts/${accountId}/engage/letters/draft/${letter.id}/edit/filters`,
                                )
                            }
                        >
                            <span>Select contacts</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}

            {!hasReturnAddressWithName && (
                <AlertNotification.Root className={'w-full'} type={'error'}>
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            No return address has been selected
                        </AlertNotification.Title>
                        <AlertNotification.Description>
                            Navigate to the 'Details' step where you can choose your return address.
                        </AlertNotification.Description>
                        <Button
                            variant="none"
                            color="error"
                            size="xs"
                            className="font-medium"
                            onClick={() =>
                                history.push(
                                    `/accounts/${accountId}/engage/letters/draft/${letter.id}/edit/details`,
                                )
                            }
                        >
                            <span>Select return address</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}

            {hasValidLetter && (
                <>
                    <Box className={twJoin(isLoadingPaymentData && 'is-loading')}>
                        <div className="px-6 py-4 text-xl font-medium">Confirm order</div>
                        <Separator />
                        <div className="flex flex-col gap-4 p-6 pt-4">
                            <div>
                                <div className="text-sm font-medium">Total</div>
                                <div className="text-2xl font-medium">
                                    {paymentData?.total_amount}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {maskWholeNumber(paymentData?.quantity ?? 0)}{' '}
                                    {pluralize(paymentData?.quantity ?? 0, 'recipient')} &times;{' '}
                                    {paymentData?.unit_price}/letter
                                </div>
                            </div>
                            <Separator />
                            <Form.Container>
                                <Label>Recipients</Label>
                                <RecipientCountButton className="text-sm" />
                                <LetterExcludedRecipientCounts />
                            </Form.Container>

                            <Separator />
                            <div className="flex flex-col gap-6">
                                <Form.Container>
                                    <Form.Label name={path('processingSpeed')}>
                                        Process order
                                    </Form.Label>
                                    <div className="flex">
                                        <Form.UseFormRadioGroup
                                            control={control}
                                            name={'processingSpeed'}
                                            variant={'toggle'}
                                        >
                                            <RadioGroup.Item value={'immediate'}>
                                                <div className={'flex items-center gap-1.5'}>
                                                    <p>Immediately</p>
                                                </div>
                                            </RadioGroup.Item>
                                            <RadioGroup.Item value={'scheduled'}>
                                                <div className={'flex items-center gap-1.5'}>
                                                    <p>Schedule</p>
                                                </div>
                                            </RadioGroup.Item>
                                        </Form.UseFormRadioGroup>
                                    </div>
                                </Form.Container>

                                {processingSpeed === 'scheduled' && (
                                    <Form.Container>
                                        <Form.Label name={path('shippingDate')}>
                                            Select process date
                                        </Form.Label>
                                        <Form.UseFormDatePicker
                                            control={control}
                                            name={path('shippingDate')}
                                            className="w-full max-w-96"
                                            minDate={DateTime.now().plus({ days: 1 })}
                                        />
                                        <Form.UseFormError name={'sending_at'} />
                                    </Form.Container>
                                )}

                                <Form.Container>
                                    <div className="flex gap-8">
                                        <div>
                                            <Form.Label name={path('letterSettings.postageType')}>
                                                Shipping speed
                                            </Form.Label>
                                            <div className="text-sm text-gray-600">
                                                {POSTAGE_TYPES['usps_first_class'].label}
                                                <br />
                                                {isFetchingLetter ? (
                                                    <> Calculating estimated delivery dates... </>
                                                ) : (
                                                    <>
                                                        {' '}
                                                        Estimated delivery {
                                                            estimatedShippingDates
                                                        }{' '}
                                                    </>
                                                )}
                                                <br />
                                                <span className="text-xs">
                                                    * Holidays may affect this estimate.
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <Form.Label name={path('letterSettings.isDoubleSided')}>
                                                Paper settings
                                            </Form.Label>
                                            <div className="text-sm text-gray-600">
                                                {letter.settings.is_double_sided
                                                    ? 'Double-sided'
                                                    : 'Single-sided'}
                                            </div>
                                        </div>
                                    </div>
                                </Form.Container>

                                <AlertNotification.Root type="info" className="bg-gray-25">
                                    <AlertNotification.Icon />
                                    <AlertNotification.Content className="flex flex-row gap-3">
                                        <div className="sm:max-w-96">
                                            <AlertNotification.Title>
                                                Review mailing preview
                                            </AlertNotification.Title>
                                            <AlertNotification.Description>
                                                Please review the generated preview of your mailing
                                                below to ensure its accuracy before ordering
                                            </AlertNotification.Description>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    <LetterMailProofDialog.Root>
                                                        <LetterMailProofDialog.Trigger asChild>
                                                            <Button
                                                                variant="outline"
                                                                color="neutral"
                                                            >
                                                                <span className="font-semibold">
                                                                    Mail proof
                                                                </span>
                                                                {letter.can_send_free_mail_proof && (
                                                                    <Badge
                                                                        size="sm"
                                                                        color="primary"
                                                                    >
                                                                        FREE!
                                                                    </Badge>
                                                                )}
                                                            </Button>
                                                        </LetterMailProofDialog.Trigger>
                                                    </LetterMailProofDialog.Root>
                                                    <div className="text-gray-500">or</div>
                                                </div>
                                                <div className="flex flex-col items-center justify-center gap-2">
                                                    <LetterConfirmMailingDialog.Root>
                                                        <LetterConfirmMailingDialog.Trigger
                                                            asChild
                                                            disabled={
                                                                isFetchingPreviewStatus ||
                                                                !previewStatus?.status
                                                            }
                                                        >
                                                            <Button
                                                                variant="solid"
                                                                className="font-semibold"
                                                            >
                                                                Ready to send
                                                            </Button>
                                                        </LetterConfirmMailingDialog.Trigger>
                                                    </LetterConfirmMailingDialog.Root>
                                                    {!previewStatus?.status && (
                                                        <div className="flex w-full items-center justify-center gap-2 text-xs font-medium text-gray-600">
                                                            <Loader
                                                                size="sm"
                                                                className="relative"
                                                            />
                                                            Generating preview
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </AlertNotification.Content>
                                </AlertNotification.Root>
                            </div>
                        </div>
                    </Box>
                    <ErrorBoundary fallback={<>Error generating preview.</>}>
                        <WrappedLetterPreview />
                    </ErrorBoundary>
                </>
            )}
        </div>
    );
};

const WrappedLetterPreview = () => {
    const query = useCurrentLetterPreview();
    const { data: previewStatus, isError, error, refetch } = query;

    if (isError) {
        return (
            <AlertNotification.Root className={'w-full'} type={'error'}>
                <AlertNotification.Icon />
                <AlertNotification.Content>
                    <AlertNotification.Title>Error</AlertNotification.Title>
                    <AlertNotification.Description>
                        {(error as GivebutterAxiosError).response?.data?.message ??
                            'There was a problem generating your letter preview.'}
                    </AlertNotification.Description>
                    <Button
                        variant="none"
                        color="error"
                        size="xs"
                        className="font-medium"
                        onClick={() => refetch()}
                    >
                        Retry
                    </Button>
                </AlertNotification.Content>
            </AlertNotification.Root>
        );
    }

    if (!previewStatus?.status) {
        return (
            <Box className="flex h-72 flex-col items-center justify-center gap-2 p-6 text-sm font-medium text-gray-600">
                <Loader className="relative" /> Generating preview
            </Box>
        );
    }

    return (
        <div className={'flex flex-col gap-1'}>
            <div className={'flex justify-end'}>
                <LetterDownloadPreviewButton variant="transparent" size="sm" />
            </div>
            <LetterPreview />
        </div>
    );
};

const LetterConfirmationWrapper = () => {
    return (
        <div className={'overflow-y-auto px-4'}>
            <div className={'mx-auto flex w-full max-w-3xl flex-col gap-4'}>
                <ErrorBoundary
                    fallbackRender={props => {
                        const error = props.error as GivebutterAxiosError;
                        const errorMessage = error?.response?.data.message;
                        const errors = error?.response?.data.errors;

                        if (!errors && !errorMessage) {
                            return null;
                        }

                        return (
                            <>
                                {errorMessage && (
                                    <AlertNotification.Root className={'w-full'} type={'error'}>
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Title>Error</AlertNotification.Title>
                                            <AlertNotification.Description>
                                                {errorMessage}
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>
                                )}
                                {errors &&
                                    Object.keys(errors).map(err => {
                                        return (
                                            <AlertNotification.Root
                                                className={'w-full'}
                                                type={'error'}
                                                key={err}
                                            >
                                                <AlertNotification.Icon />
                                                <AlertNotification.Content>
                                                    <AlertNotification.Title>
                                                        Validation error
                                                    </AlertNotification.Title>
                                                    <AlertNotification.Description>
                                                        {errors[err]}
                                                    </AlertNotification.Description>
                                                </AlertNotification.Content>
                                            </AlertNotification.Root>
                                        );
                                    })}
                            </>
                        );
                    }}
                >
                    <LetterConfirmationEasySend />
                </ErrorBoundary>
            </div>
        </div>
    );
};

const useHasAddressWithName = (addressId?: string) => {
    const { accountId } = useCurrentAccount();
    const id = addressId ? parseInt(addressId) : 0;

    const { data: returnAddress } = useSuspenseQuery({
        queryKey: accountQueryKeys.addressById(accountId, id),
        queryFn: () => conditionallyFetchOrNull(() => fetchAccountAddress(accountId, id), id > 0),
    });
    return !!returnAddress?.name && returnAddress?.name.length <= 40;
};

export { LetterConfirmationWrapper as LetterConfirmationEasySend };
